import * as React from 'react'
import { Save, Trash2, X } from 'lucide-react'
import { Modal } from '../../Modal'
import { Button } from '../../Button'
import { TextInput } from '../../Form'
import { ColorPicker } from '../../ColorPicker'
import { Checkbox } from '../../Checkbox'
import { Callout } from '../../Callout'
import { Text } from '../../Text'
import { useConfirmModal } from '../../../../app/hooks/useConfirmModal'
import { useT } from '../../../../app/lib/i18n'
import type { SavedView, SavedViewConfig } from '../../../../app/lib/api/saved-views'
import {
  createSavedView,
  updateSavedView,
  deleteSavedView,
} from '../../../../app/lib/api/saved-views'
import {
  body as confirmModalBody,
  title as confirmModalTitle,
} from '../../ConfirmModal/ConfirmModal.css'
import { IconPicker } from './IconPicker'
import { isSavedViewIconKey, type SavedViewIconKey } from './icons'
import * as s from './SavedViews.css'

const DEFAULT_COLOR = '#6B7280'
const DEFAULT_ICON: SavedViewIconKey = 'bookmark'

export interface SavedViewFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  accessToken: string
  resourceType: string
  listId: string
  /** Current snapshot to persist on create. */
  currentConfig: SavedViewConfig
  /** When set, edit mode. */
  existing?: SavedView | null
  onSaved: (view: SavedView) => void
  onDeleted?: (id: string) => void
}

export function SavedViewFormModal({
  open,
  onOpenChange,
  accessToken,
  resourceType,
  listId,
  currentConfig,
  existing,
  onSaved,
  onDeleted,
}: SavedViewFormModalProps) {
  const t = useT()
  const { confirm, ConfirmModal } = useConfirmModal()
  const [name, setName] = React.useState('')
  const [icon, setIcon] = React.useState<SavedViewIconKey>(DEFAULT_ICON)
  const [color, setColor] = React.useState(DEFAULT_COLOR)
  const [share, setShare] = React.useState(false)
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const initialRef = React.useRef({
    name: '',
    icon: DEFAULT_ICON as string,
    color: DEFAULT_COLOR,
    share: false,
  })

  React.useEffect(() => {
    if (!open) return
    setError(null)
    if (existing) {
      const nextIcon = isSavedViewIconKey(existing.icon)
        ? existing.icon
        : DEFAULT_ICON
      const next = {
        name: existing.name,
        icon: nextIcon,
        color: existing.color || DEFAULT_COLOR,
        share: existing.visibility === 'org',
      }
      setName(next.name)
      setIcon(next.icon)
      setColor(next.color)
      setShare(next.share)
      initialRef.current = next
    } else {
      const next = {
        name: '',
        icon: DEFAULT_ICON,
        color: DEFAULT_COLOR,
        share: false,
      }
      setName(next.name)
      setIcon(next.icon)
      setColor(next.color)
      setShare(next.share)
      initialRef.current = next
    }
  }, [open, existing])

  const dirty =
    name.trim() !== initialRef.current.name.trim() ||
    icon !== initialRef.current.icon ||
    color.toUpperCase() !== initialRef.current.color.toUpperCase() ||
    share !== initialRef.current.share

  const canSave = existing
    ? dirty && name.trim().length > 0
    : name.trim().length > 0

  const handleSave = async () => {
    const trimmed = name.trim()
    if (!trimmed) {
      setError('Name is required')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const visibility = share ? 'org' : 'self'
      if (existing) {
        const updated = await updateSavedView(accessToken, existing.id, {
          name: trimmed,
          icon,
          color: color.toUpperCase(),
          visibility,
        })
        onSaved(updated)
      } else {
        const created = await createSavedView(accessToken, {
          resourceType,
          listId,
          name: trimmed,
          icon,
          color: color.toUpperCase(),
          visibility,
          config: currentConfig,
        })
        onSaved(created)
      }
      onOpenChange(false)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to save view')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!existing) return
    const ok = await confirm(
      'Delete saved view?',
      `Delete “${existing.name}”? This cannot be undone.`,
      'Delete',
      'danger',
    )
    if (!ok) return
    setSaving(true)
    setError(null)
    try {
      await deleteSavedView(accessToken, existing.id)
      onDeleted?.(existing.id)
      onOpenChange(false)
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to delete view')
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      <Modal
        open={open}
        setOpen={onOpenChange}
        minWidth="520px"
        maxWidth="640px"
        minHeight="auto"
      >
        <div className={confirmModalBody}>
          <h3 className={confirmModalTitle}>
            {existing ? 'Edit saved view' : 'Save this view'}
          </h3>
          <div className={s.form}>
            {error ? <Callout variant="error">{error}</Callout> : null}

            <TextInput
              label={t('shared.savedView.name')}
              value={name}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setName(e.target.value)
              }
              placeholder="e.g. Unverified Naughty Dog Techs"
              disabled={saving}
              autoFocus
            />

            <div className={s.field}>
              <Text level="body-small" weight="medium" className={s.fieldLabel}>
                Icon
              </Text>
              <IconPicker value={icon} onChange={setIcon} color={color} />
            </div>

            <ColorPicker
              label={t('shared.savedView.color')}
              value={color}
              onChange={setColor}
              disabled={saving}
            />

            <label className={s.shareRow}>
              <Checkbox
                id="saved-view-share"
                checked={share}
                onCheckedChange={setShare}
                aria-label={t('shared.savedView.shareWithOrganization')}
                disabled={saving}
              />
              <span className={s.shareLabel}>{t('shared.savedView.shareWithOrganization')}</span>
            </label>

            <div className={s.formFooterBar}>
              <div className={s.formFooterLeft}>
                {existing ? (
                  <Button
                    type="button"
                    color="danger"
                    variant="outline"
                    size="md"
                    iconLeft={<Trash2 size={16} />}
                    disabled={saving}
                    onClick={() => void handleDelete()}
                  >
                    Delete
                  </Button>
                ) : null}
              </div>
              <div className={s.formFooterRight}>
                <Button
                  color="neutral"
                  variant="outline"
                  size="md"
                  iconLeft={<X size={16} />}
                  disabled={saving}
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  size="md"
                  iconLeft={<Save size={16} />}
                  disabled={!canSave || saving}
                  loading={saving}
                  onClick={() => void handleSave()}
                >
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </Modal>
      <ConfirmModal />
    </>
  )
}
